/**
 * What the one status screen of Make a room says on a phone and an iPad: how far the step is and the one line under the
 * bar, all from what the server counted (pieces encoded, audio heard, read steps landed). Nothing here counts on a timer.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { LIVE_SCENE_ORDER, READ_PAGE_STEP, type SceneStep } from "../../../shared/board-copy";
import type { BoardStateResponse, PagePairsResponse } from "../../../shared/types";
import { api, ApiError } from "../../lib/api";
import { pagesOf } from "../make/designPages";
import { LIVE_SCENE_NAMES } from "../make/MakeLive";
import { heardShare, SCENE_NAMES, SCENES } from "../make/MakeSteps";
import type { BoardPhase } from "../progress";

/** The steps a recording's status screen stands on, by index in the seven. */
const REC_AT: Partial<Record<BoardPhase, number>> = { playable: 1, listen: 2, build: 4, design: 6 };
/** A live's status screen: the page being written, then designed, by index in the four. */
const LIVE_AT: Partial<Record<BoardPhase, number>> = { write: 2, design: 3 };

export function workStepIndex(phase: BoardPhase, live: boolean): number {
  return (live ? LIVE_AT : REC_AT)[phase] ?? 0;
}

/** The share of the running step done, 0 to 1. */
export function stepShare(phase: BoardPhase, live: boolean, server: BoardStateResponse | null): number {
  if (!server) return 0;
  if (phase === "playable") {
    const v = server.video;
    return v.chunkCount > 0 ? Math.min(1, v.chunksDone / v.chunkCount) : 0;
  }
  if (phase === "listen") return heardShare(server.video);
  if (phase === "build" || phase === "write") {
    const order = live ? LIVE_SCENE_ORDER : SCENES;
    const landed = new Set(server.readProgress ?? []);
    return order.filter((s) => landed.has(s)).length / order.length;
  }
  return 0;
}

/** The whole bar, 0 to 100, over all the steps of the kind. */
export function overallPercent(phase: BoardPhase, live: boolean, server: BoardStateResponse | null): number {
  const total = live ? 4 : 7;
  return Math.round(((workStepIndex(phase, live) + stepShare(phase, live, server)) / total) * 100);
}

/** The scene being written now: the first the read has not landed. */
function runningScene(live: boolean, server: BoardStateResponse | null): SceneStep {
  const landed = new Set(server?.readProgress ?? []);
  const order = live ? LIVE_SCENE_ORDER : SCENES;
  return order.find((s) => !landed.has(s)) ?? READ_PAGE_STEP;
}

/** The line under the bar. */
export function workLine(phase: BoardPhase, server: BoardStateResponse | null, durationSec: number | null): string {
  if (phase === "playable") {
    const v = server?.video;
    return v && v.chunkCount > 0 ? `${v.chunksDone} of ${v.chunkCount} pieces ready` : "Pieces that play anywhere";
  }
  if (phase === "listen") {
    const minutes = durationSec ? Math.max(1, Math.round(durationSec / 60)) : 0;
    const share = server ? heardShare(server.video) : 0;
    return minutes ? `${Math.round(share * minutes)} of ${minutes} minutes` : "Every word, with its second";
  }
  if (phase === "build") return SCENE_NAMES[runningScene(false, server)];
  if (phase === "write") return "About a minute";
  return "Laying out the page";
}

/** What the status screen is titled. */
export function workTitle(phase: BoardPhase, server: BoardStateResponse | null): string {
  if (phase === "playable") return "Making it playable";
  if (phase === "listen") return "Listening to the talk";
  if (phase === "build") return "Building the board";
  if (phase === "write") return LIVE_SCENE_NAMES[runningScene(true, server)] ?? "Writing the page";
  return "Designing the page";
}

type Pair = PagePairsResponse["pairs"][number];

const POLL_MS = 2000;

/**
 * The phone does not ask for two pictures or offer a choice: it takes the page that is ready. "Make another" and
 * the look closer stay on a computer.
 */
export function useAutoDesign(webinarId: string, active: boolean, onChosen: () => void) {
  const [error, setError] = useState("");
  const [round, setRound] = useState(0);
  const chosen = useRef(false);
  const done = useRef(onChosen);
  done.current = onChosen;

  useEffect(() => {
    if (!active) return;
    let alive = true;
    chosen.current = false;
    const settled = (pair: Pair | undefined) => !!pair && pagesOf([pair]).every((c) => c.status !== "making");
    const look = async () => {
      let got: PagePairsResponse;
      try {
        got = await api.pages.list(webinarId);
      } catch {
        return;
      }
      if (!alive || chosen.current) return;
      const newest = got.pairs.length ? got.pairs[got.pairs.length - 1] : undefined;
      const ready = newest ? pagesOf([newest]).find((c) => c.status === "ready" && c.html) : undefined;
      if (ready) {
        chosen.current = true;
        try {
          await api.pages.choose(webinarId, { id: ready.id });
          if (alive) done.current();
        } catch (e) {
          chosen.current = false;
          if (alive) setError(e instanceof ApiError ? e.message : "That page could not be used. Try again.");
        }
        return;
      }
      if (settled(newest)) setError("The page did not land. Try again.");
    };
    void look();
    const timer = window.setInterval(() => void look(), POLL_MS);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [webinarId, active, round]);

  const retry = useCallback(() => {
    setError("");
    void api.pages
      .make(webinarId)
      .then(() => setRound((n) => n + 1))
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : "Another page could not be started. Try again."));
  }, [webinarId]);

  return { error, retry };
}
